import axios from 'axios';
import { supabase } from './supabaseClient';
import { isDemoModeEnabled } from './featureFlags';

// Vite uses import.meta.env instead of process.env
const WEATHER_API_KEY = import.meta.env.VITE_WEATHER_API_KEY || '';
const WEATHER_API_BASE_URL = 'https://api.openweathermap.org/data/2.5';

interface WeatherData {
  temperature: number;
  humidity: number;
  windSpeed: number;
  description: string;
  icon: string;
}

interface AgriculturalUpdate {
  id: string;
  title: string;
  description: string;
  timestamp: string;
  type: 'alert' | 'info' | 'warning';
}

const MOCK_AGRICULTURAL_UPDATES: AgriculturalUpdate[] = [
  {
    id: '1',
    title: 'Optimal Planting Time',
    description: 'The next two weeks are optimal for planting maize in the central region due to predicted rainfall patterns.',
    timestamp: new Date(Date.now() - 3600000).toISOString(),
    type: 'info'
  },
  {
    id: '2',
    title: 'Pest Alert',
    description: 'Fall armyworm outbreaks have been reported in the western region. Preventive measures recommended.',
    timestamp: new Date(Date.now() - 86400000).toISOString(),
    type: 'warning'
  },
  {
    id: '3',
    title: 'Market Update',
    description: 'Potato prices expected to rise in the coming weeks due to supply shortages. Consider timing your harvest accordingly.',
    timestamp: new Date(Date.now() - 172800000).toISOString(),
    type: 'info'
  }
];

const MOCK_AGRICULTURAL_ALERTS: AgriculturalUpdate[] = [
  {
    id: '4',
    title: 'Heavy Rainfall Warning',
    description: 'Heavy rainfall expected in the next 48 hours in the highlands region. Secure your crops and livestock.',
    timestamp: new Date(Date.now() - 7200000).toISOString(),
    type: 'alert'
  },
  {
    id: '5',
    title: 'Disease Outbreak',
    description: 'Tomato blight detected in several farms in the eastern region. Inspect your crops and apply fungicide if needed.',
    timestamp: new Date(Date.now() - 43200000).toISOString(),
    type: 'warning'
  }
];

class WeatherService {
  async getWeatherByCoordinates(lat: number, lon: number): Promise<WeatherData> {
    const response = await axios.get(
      `${WEATHER_API_BASE_URL}/weather?lat=${lat}&lon=${lon}&units=metric&appid=${WEATHER_API_KEY}`
    );
    return {
      temperature: response.data.main.temp,
      humidity: response.data.main.humidity,
      windSpeed: response.data.wind.speed,
      description: response.data.weather[0].description,
      icon: response.data.weather[0].icon
    };
  }

  async getWeatherByCity(city: string): Promise<WeatherData> {
    const response = await axios.get(
      `${WEATHER_API_BASE_URL}/weather?q=${city}&units=metric&appid=${WEATHER_API_KEY}`
    );
    return {
      temperature: response.data.main.temp,
      humidity: response.data.main.humidity,
      windSpeed: response.data.wind.speed,
      description: response.data.weather[0].description,
      icon: response.data.weather[0].icon
    };
  }

  async getAgriculturalUpdates(): Promise<AgriculturalUpdate[]> {
    try {
      const { data, error } = await supabase
        .from('agricultural_updates')
        .select('*')
        .order('timestamp', { ascending: false });

      if (error) {
        console.warn('Failed to fetch updates from database:', error);
        return isDemoModeEnabled() ? MOCK_AGRICULTURAL_UPDATES : [];
      }

      return data?.map(update => ({
        id: update.id,
        title: update.title,
        description: update.description,
        timestamp: update.timestamp,
        type: update.type as 'alert' | 'info' | 'warning'
      })) || [];
    } catch (error) {
      console.error('Error fetching agricultural updates:', error);
      return isDemoModeEnabled() ? MOCK_AGRICULTURAL_UPDATES : [];
    }
  }

  async getForecast(lat: number, lon: number, _days: number = 5) {
    const response = await axios.get(
      `${WEATHER_API_BASE_URL}/forecast?lat=${lat}&lon=${lon}&units=metric&appid=${WEATHER_API_KEY}`
    );
    return response.data;
  }

  async getAgriculturalAlerts(): Promise<AgriculturalUpdate[]> {
    try {
      // Try to fetch from Supabase first
      const { data, error } = await supabase
        .from('weather_alerts')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.warn('Failed to fetch alerts from database:', error);
        return isDemoModeEnabled() ? MOCK_AGRICULTURAL_ALERTS : [];
      }

      // Transform Supabase data to match interface
      const mapped: AgriculturalUpdate[] = (data ?? []).map((alert: any) => {
        const type: AgriculturalUpdate['type'] =
          alert.severity === 'critical' ? 'alert' : alert.severity === 'high' ? 'warning' : 'info';

        return {
          id: alert.id,
          title: `${alert.alert_type.charAt(0).toUpperCase() + alert.alert_type.slice(1)} Alert`,
          description: alert.message,
          timestamp: alert.created_at,
          type,
        };
      });
      return mapped.length > 0 ? mapped : (isDemoModeEnabled() ? MOCK_AGRICULTURAL_ALERTS : []);
    } catch (error) {
      console.error('Error fetching agricultural alerts:', error);
      return isDemoModeEnabled() ? MOCK_AGRICULTURAL_ALERTS : [];
    }
  }

  // New Supabase integration methods
  async getWeatherDataFromDB(countyId: string): Promise<WeatherData | null> {
    try {
      const { data, error } = await supabase
        .from('weather_data')
        .select('*')
        .eq('county_id', countyId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) {
        console.warn('Failed to fetch weather from database:', error);
        return null;
      }

      return {
        temperature: data.temperature,
        humidity: data.humidity,
        windSpeed: data.wind_speed,
        description: data.weather_condition,
        icon: this.getWeatherIcon(data.weather_condition)
      };
    } catch (error) {
      console.error('Error fetching weather from database:', error);
      return null;
    }
  }

  async storeWeatherData(countyId: string, weatherData: WeatherData): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('weather_data')
        .insert([
          {
            county_id: countyId,
            temperature: weatherData.temperature,
            humidity: weatherData.humidity,
            wind_speed: weatherData.windSpeed,
            weather_condition: weatherData.description,
            forecast_date: new Date().toISOString()
          }
        ]);

      if (error) {
        console.error('Failed to store weather data:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error storing weather data:', error);
      return false;
    }
  }

  async createWeatherAlert(countyId: string, alertType: string, severity: string, message: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('weather_alerts')
        .insert([
          {
            county_id: countyId,
            alert_type: alertType,
            severity: severity,
            message: message,
            start_date: new Date().toISOString(),
            is_active: true
          }
        ]);

      if (error) {
        console.error('Failed to create weather alert:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error creating weather alert:', error);
      return false;
    }
  }

  private getWeatherIcon(condition: string): string {
    const conditionLower = condition.toLowerCase();
    if (conditionLower.includes('clear')) return '01d';
    if (conditionLower.includes('cloud')) return '02d';
    if (conditionLower.includes('rain')) return '10d';
    if (conditionLower.includes('storm')) return '11d';
    if (conditionLower.includes('snow')) return '13d';
    if (conditionLower.includes('mist') || conditionLower.includes('fog')) return '50d';
    return '01d'; // default
  }
}

export const weatherService = new WeatherService();
